import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Move } from "lucide-react";
import { useInit } from "@follosoft/react-components";
import { cn } from "@/lib/utils";
import { resolveFrameHeight } from "@/utils/frame-height";

// Paper dimensions in CSS pixels at 96 dpi.
const PAPER_SIZES = {
  letter: { width: 816, height: 1056 },
  legal: { width: 816, height: 1344 },
  a4: { width: 794, height: 1123 },
} as const;

type PaperSize = keyof typeof PAPER_SIZES;

const LETTER_FLAG = "account.contract.paper-size.letter";
const LEGAL_FLAG = "account.contract.paper-size.legal";

interface Props {
  /** Number of paper pages worth of height to reserve for the document. */
  pages?: number;
  /** Height the document reported, in CSS pixels. See resolveFrameHeight. */
  documentHeight?: number;
  className?: string;
  children: ReactNode;
}

/**
 * Renders the contract document at its real paper width so it keeps the layout
 * of the printed PDF instead of reflowing. On narrow screens the paper stays at
 * full size and the frame scrolls in both directions, so users pan around the
 * document the way they would in a PDF viewer.
 */
const ContractPaperFrame: FC<Props> = ({
  pages = 1,
  documentHeight,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const { isFeatureFlagEnabled } = useInit();

  const hasFlag = (name: string) => !!isFeatureFlagEnabled?.(name);

  // Same order as the backend's Contract::pageSize(), which checks letter before legal.
  const paperSize: PaperSize = hasFlag(LETTER_FLAG)
    ? "letter"
    : hasFlag(LEGAL_FLAG)
      ? "legal"
      : "a4";

  const { width: paperWidth, height: paperHeight } = PAPER_SIZES[paperSize];
  const paperTotalHeight = paperHeight * Math.max(1, pages);
  const frameHeight = resolveFrameHeight(documentHeight, paperTotalHeight);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Move size={14} className="shrink-0" />
        <span className="font-medium text-foreground">
          {t("fe.pages.flow.steps.contract.document-label")}
        </span>
        <span className="hidden sm:inline">·</span>
        <span>{t("fe.pages.flow.steps.contract.document-hint")}</span>
      </div>
      <div
        className={cn(
          // Below sm: the height cap keeps this a real vertical scroller, so containment
          // has to stay or a drag chains into the page and trips pull-to-refresh. Above it
          // the frame is as tall as the document, so only the horizontal pan needs holding.
          "w-full max-w-full min-w-0 overflow-auto overscroll-contain sm:overscroll-x-contain max-h-[80vh] sm:max-h-none",
          className,
        )}
      >
        <div
          style={{
            width: paperWidth,
            height: frameHeight,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ContractPaperFrame;
